import { CheckCircle2, Mail, AlertTriangle, HelpCircle, Building2, Ban, Clock, Send } from 'lucide-preact'
import { BRAND_CLASSES } from '@/constants/theme'
import type { InviteAccessState, InviteDeliveryState } from '@/types/userRoles'

const PILL = 'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap'

interface Presentation {
  label: string
  classes: string
  icon: typeof Mail
  /** Shown on hover. Says what the state means, not what it is called. */
  title: string
}

// Hue follows the house meaning rather than the state's name: green is done, amber needs attention, red is
// broken, blue is in flight, slate is inert. Every state carries a word — a colour alone is never a status.
const ACCESS: Record<InviteAccessState, Presentation> = {
  Active: {
    label: 'Active',
    classes: BRAND_CLASSES.STATUS.SUCCESS,
    icon: CheckCircle2,
    title: 'Accepted their invitation and can sign in.',
  },
  Invited: {
    label: 'Invited',
    classes: BRAND_CLASSES.STATUS.INFO,
    icon: Mail,
    title: 'Invitation sent and still valid. They have not accepted it yet.',
  },
  InviteExpired: {
    label: 'Invite expired',
    classes: BRAND_CLASSES.STATUS.PENDING,
    icon: Clock,
    title: 'Their invitation ran out before they accepted it. Re-invite to send a fresh link.',
  },
  NoInvitationOnRecord: {
    label: 'No invitation',
    classes: BRAND_CLASSES.STATUS.PENDING,
    icon: AlertTriangle,
    title: 'They are not a member and no invitation is outstanding. Re-invite to send one.',
  },
  SignedInNotAMember: {
    label: 'Signed in, not a member',
    classes: BRAND_CLASSES.STATUS.PENDING,
    icon: AlertTriangle,
    title: 'They have signed in but never joined this organization, so they cannot reach it. Send a fresh invitation — if they click it while signed in as another account, it is consumed without joining them, so they may need to sign out first.',
  },
  NoInviteRequired: {
    label: 'No invite required',
    classes: BRAND_CLASSES.STATUS.DEFAULT,
    icon: Building2,
    title: 'They belong to another company and reach this one through their roles. They were never invited here.',
  },
  StatusUnavailable: {
    label: 'Status unavailable',
    classes: BRAND_CLASSES.STATUS.DEFAULT,
    icon: HelpCircle,
    title: 'Their sign-in status could not be read just now. This is not a statement about their access.',
  },
}

const DELIVERY: Record<InviteDeliveryState, Presentation> = {
  Delivered: {
    label: 'Delivered',
    classes: BRAND_CLASSES.STATUS.DEFAULT,
    icon: CheckCircle2,
    title: 'The mail provider accepted and delivered the invitation.',
  },
  Bounced: {
    label: 'Bounced',
    classes: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400',
    icon: AlertTriangle,
    title: 'The invitation could not be delivered to this address.',
  },
  Suppressed: {
    label: 'Suppressed',
    classes: BRAND_CLASSES.STATUS.DEFAULT,
    icon: Ban,
    title: 'This address is on the suppression list, usually after a permanent bounce, so nothing was sent.',
  },
  BlockedByEnvironmentGuard: {
    label: 'Blocked here',
    classes: BRAND_CLASSES.STATUS.DEFAULT,
    icon: Ban,
    title: 'This environment only delivers to allowlisted addresses. Expected outside production.',
  },
  Queued: {
    label: 'Queued',
    classes: BRAND_CLASSES.STATUS.DEFAULT,
    icon: Send,
    title: 'Sent, waiting on the provider to confirm delivery. This usually takes a moment.',
  },
  NeverSent: {
    label: 'Never sent',
    classes: BRAND_CLASSES.STATUS.PENDING,
    icon: AlertTriangle,
    title: 'No invitation email left our side for this person.',
  },
  Unknown: {
    label: 'Unknown',
    classes: BRAND_CLASSES.STATUS.DEFAULT,
    icon: HelpCircle,
    title: 'Delivery records could not be read just now.',
  },
}

export function InviteAccessBadge({ state }: { state: InviteAccessState }) {
  const presentation = ACCESS[state]
  if (!presentation) return null

  const Icon = presentation.icon
  return (
    <span className={`${PILL} ${presentation.classes}`} title={presentation.title}>
      <Icon className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
      {presentation.label}
    </span>
  )
}

export function InviteDeliveryBadge({ state }: { state: InviteDeliveryState }) {
  const presentation = DELIVERY[state]
  if (!presentation) return null

  const Icon = presentation.icon
  return (
    <span className={`${PILL} ${presentation.classes}`} title={presentation.title}>
      <Icon className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
      {presentation.label}
    </span>
  )
}
